import PersonIcon from "@mui/icons-material/Person";
import { Avatar, Tooltip } from "@mui/material";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import Cookies from "universal-cookie";
import {
  decodeJWT,
  getTokensByCode,
  getUrlParams,
  isTokenValid,
  login,
  setTokenCookies,
} from "../helpers/auth";
import { generateStyles, getImageURL } from "../helpers/utils";
import {
  EDefaultConfigValues,
  IMenuButton,
  IUserProfile,
  TrustedWidgetConfig,
} from "../types";
import { AccountPopover } from "./AccountPopover";
import styles from "./Widget.module.css";
import { CustomButton } from "./CustomButton";

const cookies = new Cookies();

interface WidgetProps {
  config: TrustedWidgetConfig;
}

export const TrustedWidget: React.FC<WidgetProps> = ({ config }) => {
  const [token, setToken] = useState<string>("");
  const [profile, setProfile] = useState<IUserProfile | null>(null);
  const [accountsMenu, setAccountsMenu] = useState<IMenuButton[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const customStyles = generateStyles(config);

  useEffect(() => {
    const auth = async () => {
      try {
        const { accessToken, idToken, refreshToken, expiresIn } =
          await getTokensByCode(config);

        const isValidToken = await isTokenValid(idToken);
        const isDecodeJWT = decodeJWT(idToken);
        if (isValidToken && isDecodeJWT) {
          setToken(accessToken);
          setTokenCookies(accessToken, refreshToken, expiresIn);
          window.location.replace("/");
        } else {
          console.log("auth: Token validation failed, not setting tokens");
        }
      } catch (e) {
        console.log("auth error: " + e);
      }
    };

    const accessToken = cookies.get("accessToken");

    if (accessToken && !token) {
      setToken(accessToken);
      return;
    }

    const code = getUrlParams();

    if (!accessToken && !token && !code && config?.withOutHomePage) {
      login(config);
    }

    if (!accessToken && !token) {
      auth();
    }
  }, []); // Пустой массив зависимостей - выполняется только один раз

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        let option: RequestInit = {
          credentials: "include",
        };

        if (
          (config.issuer || EDefaultConfigValues.issuer) === config.backendUrl
        ) {
          option = {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          };
        }

        const response = await fetch(
          config?.backendUrl +
            (config?.getUserInfoEndPoint ||
              EDefaultConfigValues.getUserInfoEndPoint),
          option
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "fetchUserData: HTTP error response:",
            response.status,
            errorText
          );
          return;
        }

        const data = await response.json();
        setProfile(data);
        setAccountsMenu(data.lk);
      } catch (error) {
        console.error("fetchUserData: Network/Parse error:", error);
      }
    };

    if (token) {
      fetchUserData();
    }
  }, [token]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (token && profile) {
    return (
      <div>
        <Tooltip title="Личный аккаунт" arrow>
          <button onClick={handleClick} className={styles.button}>
            {profile?.picture ? (
              <Avatar
                src={getImageURL(
                  profile.picture,
                  config?.issuer || EDefaultConfigValues.issuer
                )}
                alt={profile?.given_name || profile?.nickname || "User"}
                className={styles.buttonImage}
              />
            ) : (
              <Avatar className={clsx(styles.buttonImage)}>
                <PersonIcon
                  style={{
                    color: customStyles.text,
                  }}
                />
              </Avatar>
            )}
          </button>
        </Tooltip>

        <AccountPopover
          isOpen={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          profile={profile}
          accountsMenu={accountsMenu}
          config={config}
        />
      </div>
    );
  }

  return (
    <div className={styles.trustedWidget}>
      <CustomButton
        config={config}
        content={{
          text: config?.enterButtonText || EDefaultConfigValues.enterButtonText,
          link: "",
          type: "login",
        }}
        customStyles={customStyles.primaryButton}
      />
    </div>
  );
};
